'use client';

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useLanguage } from '@/lib/i18n/language-provider';
import { useRouter } from 'next/navigation';
import {
  Timer,
  Crown,
  ChevronUp,
  ChevronDown,
  Sparkles,
} from 'lucide-react';
import { DEMO_CONFIG, type DemoState } from '@/hooks/use-demo-mode';
import { navigateOrOpenBrowser } from '@/lib/tauri-navigation';

interface DemoTimerProps {
  state: DemoState;
  formatTime: (seconds: number) => string;
  onEndDemo: () => void;
}

export function DemoTimer({ state, formatTime, onEndDemo }: DemoTimerProps) {
  const { t } = useLanguage();
  const router = useRouter();
  const [isExpanded, setIsExpanded] = useState(true);

  const timeProgress = (state.timeRemaining / DEMO_CONFIG.SESSION_DURATION) * 100;
  const isLowTime = state.timeRemaining < 60;
  const isVeryLowTime = state.timeRemaining < 30;

  return (
    <div
      className={`
        fixed top-20 right-4 z-50 rounded-xl border-2 shadow-2xl backdrop-blur-md transition-all duration-300
        ${isVeryLowTime 
          ? 'border-red-500 bg-red-500/10 animate-pulse' 
          : isLowTime 
            ? 'border-orange-500 bg-orange-500/10' 
            : 'border-primary/30 bg-background/95'
        }
      `}
    >
      {/* Header - Always visible */}
      <div
        className="flex items-center justify-between p-3 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          <Badge
            variant="secondary"
            className={`
              ${isVeryLowTime 
                ? 'bg-red-500/20 text-red-500 border-red-500/30' 
                : isLowTime 
                  ? 'bg-orange-500/20 text-orange-500 border-orange-500/30' 
                  : 'bg-primary/10 text-primary border-primary/20'
              }
            `}
          >
            <Sparkles className="h-3 w-3 mr-1" />
            {t('demo.badge')}
          </Badge>
          
          <div className={`
            flex items-center gap-1 font-mono font-bold text-lg
            ${isVeryLowTime ? 'text-red-500' : isLowTime ? 'text-orange-500' : 'text-foreground'}
          `}>
            <Timer className="h-4 w-4" />
            {formatTime(state.timeRemaining)}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <div className="px-3 pb-3 space-y-3 border-t border-border/50 pt-3">
          {/* Time progress bar */}
          <div className="space-y-1">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{t('demo.timeRemaining')}</span>
              <span>{Math.ceil(timeProgress)}%</span>
            </div>
            <Progress
              value={timeProgress}
              className={`h-2 ${isVeryLowTime ? '[&>div]:bg-red-500' : isLowTime ? '[&>div]:bg-orange-500' : ''}`}
            />
          </div>

          {/* Demo info */}
          <p className="text-xs text-muted-foreground">
            {t('demo.limitations')}
          </p>

          {/* CTA Upgrade */}
          <Button
            size="sm"
            className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white"
            onClick={() => navigateOrOpenBrowser('/pro', router)}
          >
            <Crown className="h-4 w-4 mr-1.5" />
            {t('demo.upgradeNow')}
          </Button>
        </div>
      )}
    </div>
  );
}
